import { Box, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DealerField } from '../../components/form';
import ManufacturerField from '../../components/form/manufacturer-field';
import TextInput from '../../components/form/text-input';
import { useAppSelector } from '../../store/hooks';
import { Dealer, Manufacturer, UserRole } from '../../types';
import { selectCurrentUser, selectManageDealers, selectManageManufacturers } from '../user';
import { OrderFormData } from './new-order-wizard';

interface StepOneProps {
  onFormChange: () => void;
  manufacturers: Manufacturer[];
  isLoadingManufacturers: boolean;
  dealers?: Dealer[];
  isLoadingDealers?: boolean;
}

const StepOne: React.FC<StepOneProps> = ({
  onFormChange,
  manufacturers,
  isLoadingManufacturers,
  dealers = [],
  isLoadingDealers = false,
}) => {
  const { control, resetField } = useFormContext<OrderFormData>();
  const [userChangedManufacturer, setUserChangedManufacturer] = useState(false);
  const [userChangedDealer, setUserChangedDealer] = useState(false);
  const canManageManufacturers = useAppSelector(selectManageManufacturers);
  const canManageDealers = useAppSelector(selectManageDealers);
  const currentUser = useAppSelector(selectCurrentUser);

  // Watch manufacturer ID to filter dealers
  const manufacturerId = useWatch({
    control,
    name: 'manufacturerId',
  });

  // Watch dealerId to detect changes
  const dealerId = useWatch({
    control,
    name: 'dealerId',
  });

  // Watch other form fields
  const po = useWatch({
    control,
    name: 'po',
  });

  const jobName = useWatch({
    control,
    name: 'jobName',
  });

  const handleManufacturerChange = (_manufacturerId: string) => {
    setUserChangedManufacturer(true);
  };

  const handleDealerChange = (_dealerId: string) => {
    setUserChangedDealer(true);
  };

  // Clear dealer when manufacturer changes
  useEffect(() => {
    if (manufacturerId && canManageManufacturers && userChangedManufacturer) {
      resetField('dealerId');
      resetField('shippingAddress');
      setUserChangedManufacturer(false);
    }
  }, [manufacturerId, resetField, canManageManufacturers, userChangedManufacturer]);

  useEffect(() => {
    if (dealerId && canManageDealers && userChangedDealer) {
      resetField('shippingAddress');
      setUserChangedDealer(false);
    }
  }, [dealerId, resetField, canManageDealers, userChangedDealer]);

  // Single combined effect to detect all relevant form field changes
  useEffect(() => {
    // Only call onFormChange when any meaningful value changes
    if (
      (canManageManufacturers && manufacturerId !== undefined) ||
      (canManageDealers && dealerId !== undefined) ||
      po ||
      jobName
    ) {
      onFormChange();
    }
  }, [
    manufacturerId,
    dealerId,
    po,
    jobName,
    canManageManufacturers,
    canManageDealers,
    onFormChange,
  ]);

  // Find the manufacturer name from the manufacturer ID
  const getManufacturerName = () => {
    const selected = manufacturers.find(m => m.id === manufacturerId);
    return selected?.licenseeName ?? '';
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box sx={{ width: 4, height: 24, bgcolor: 'primary.main', borderRadius: 1, mr: 1 }} />
          <Typography variant="h6">Order Information</Typography>
        </Box>

        {/* Conditional instruction text based on user role */}
        {currentUser && (
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontStyle: 'italic' }}>
            {currentUser.role === UserRole.DEALER
              ? 'Please enter your information'
              : 'Please enter the Dealer information'}
          </Typography>
        )}
      </Grid>

      {/* Manufacturer field - only show if user can manage manufacturers */}
      {canManageManufacturers && (
        <ManufacturerField
          name="manufacturerId"
          control={control}
          manufacturers={manufacturers}
          isReadOnly={false}
          required={true}
          getManufacturerName={getManufacturerName}
          isLoading={isLoadingManufacturers}
          onManufacturerChange={handleManufacturerChange}
        />
      )}

      {/* Dealer field - only show if user can manage dealers */}
      {canManageDealers && (
        <DealerField
          name="dealerId"
          label={'Customer'}
          control={control}
          dealers={dealers}
          isReadOnly={false}
          required={true}
          isLoading={isLoadingDealers}
          requiresManufacturer={true}
          hasManufacturer={!!manufacturerId}
          onDealerChange={handleDealerChange}
        />
      )}

      {/* Purchase Order Number */}
      <Grid item xs={12} md={6}>
        <TextInput name="po" control={control} label="PO #" isReadOnly={false} required={true} />
      </Grid>

      {/* Job Name */}
      <Grid item xs={12} md={6}>
        <TextInput
          name="jobName"
          control={control}
          label="Job Name"
          isReadOnly={false}
          required={true}
        />
      </Grid>
    </Grid>
  );
};

export default StepOne;
